/**
 * Types of field-level changes that can occur
 */
export enum LogItemChangeType {
  ADD = 'add',
  EDIT = 'edit',
  REMOVE = 'remove'
}

/**
 * Types of document-level operations
 */
export enum LogEntryChangeType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete'
}

/**
 * Represents a single field-level change in a log entry.
 * Each log item describes a change to a specific field in the document.
 *
 * @see {@link https://github.com/granitebps/mongoose-log-history mongoose-log-history documentation}
 */
export interface LogItem {
  /**
   * Unique identifier for the log item
   * we used any because ObjectId was failing the build, but it should be a string in the FE and ObjectId in the BE
   */
  _id: any;

  /**
   * The path of the field that changed (e.g., "status", "items.0.qty")
   */
  field_name: string;

  /**
   * The value before the change
   */
  from_value: any;

  /**
   * The value after the change
   */
  to_value: any;

  /**
   * The type of change: 'add', 'edit', or 'remove'
   */
  change_type: LogItemChangeType;
}

/**
 * Represents a complete log entry for document changes.
 *
 * @see {@link https://github.com/granitebps/mongoose-log-history mongoose-log-history documentation}
 */
export interface LogEntry {
  /**
   * Unique identifier for the log entry
   * we used any because ObjectId was failing the build, but it should be a string in the FE and ObjectId in the BE
   */
  _id: any;

  /**
   * The name of the model/collection that was changed
   */
  model: string;

  /**
   * The ID of the document that was changed
   * we used any because ObjectId was failing the build, but it should be a string in the FE and ObjectId in the BE
   */
  model_id: any;

  /**
   * The type of operation: 'create', 'update', or 'delete'
   */
  change_type: LogEntryChangeType;

  /**
   * Array of field-level changes
   */
  logs: LogItem[];

  /**
   * Information about who made the change.
   * Null for system-generated changes with no actor (e.g. no-show alerts).
   */
  created_by: any;

  /**
   * Timestamp when the log entry was created
   */
  created_at: Date;
}
